import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import EvaluationStatus from './Utils/EvaluationStatus';
import Navigation from '../Pages/Components/Navigation';
import EvaluationQuestionnaire from './EvaluationQuestionnaire';
import EvaluationChallenge from './EvaluationChallenge';
import EvaluationSettings from './EvaluationSettings';

/**
 * EvaluationDetails Component
 * Standalone page that displays evaluation details
 * Gets evaluation ID from URL query parameter (?eid=...)
 */
const EvaluationDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useSession();
  
  // State for active tab
  const [activeTab, setActiveTab] = useState('questionnaire');
  
  // State for evaluation data
  const [evaluationData, setEvaluationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract evaluation ID from URL query parameters
  const getEvaluationId = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('eid');
  };

  /**
   * Handles API errors, especially 401 unauthorized errors
   */
  const handleApiError = (err) => {
    console.error('API Error:', err);
    console.log('Error status code:', err.status);
    
    if (err.status === 401) {
      console.log('Session expired or unauthorized. Redirecting to login page.');
      logout();
      navigate('/login');
      return true;
    }
    
    return false;
  };

  /**
   * Handles tab change and updates URL hash
   */
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    navigate(`${location.pathname}${location.search}#${tabName}`, { replace: true });
    window.scrollTo(0, 0);
  };

  // Function to fetch evaluation details from the API
  const fetchEvaluationDetails = async () => {
    const evaluationId = getEvaluationId();
    
    if (!evaluationId) {
      setError('Evaluation ID is missing from URL');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    try {
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }
      
      const url = new URL(API_ENDPOINTS.GET_EVALUATION_DETAILS);
      url.searchParams.append('eid', evaluationId);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });
      
      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }
      
      const data = await response.json();
      console.log('Evaluation details data:', data);
      
      const transformedData = Array.isArray(data) && data.length > 0 ? data[0] : data;
      
      setEvaluationData(transformedData);
      setError(null);
    } catch (err) {
      if (!handleApiError(err)) {
        console.error('Error fetching evaluation details:', err);
        
        if (err.status === 401) {
          setError('Your session has expired. Please log in again.');
        } else {
          setError('Failed to load evaluation details. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch evaluation details when evaluationId changes
  useEffect(() => {
    fetchEvaluationDetails();
  }, [location.search]);

  // Check for hash fragment in URL and set active tab accordingly
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && ['questionnaire', 'challenge', 'settings'].includes(hash)) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  // Set document title
  useEffect(() => {
    if (evaluationData?.name) {
      document.title = `${evaluationData.name} - Evaluation Details`;
    } else {
      document.title = "Evaluation Details";
    }
    
    return () => {
      document.title = "VaaS Admin";
    };
  }, [evaluationData]);

  // Render tab button
  const renderTabButton = (tabName, label) => (
    <button
      onClick={() => handleTabChange(tabName)}
      className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
        activeTab === tabName
          ? 'border-blue-500 text-blue-600'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-100 p-5 pt-[76px]">
      <Navigation 
        title={
          loading ? (
            <span className="inline-flex items-center">
              <span className="animate-pulse">Loading evaluation details...</span>
            </span>
          ) : evaluationData ? (
            <span className="font-normal text-2xl">
              <span className="font-bold">{evaluationData.name}</span>
              {evaluationData.jobs && (
                <>
                  {' for '}<span className="font-bold">{evaluationData.jobs.job_name}</span>
                  {' at '}<span className="font-bold">{evaluationData.jobs.company_name}</span>
                </>
              )}
              {evaluationData.talent && (
                <>
                  {' for '}<span className="font-bold">{evaluationData.talent.fname} {evaluationData.talent.lname}</span>
                </>
              )}
            </span>
          ) : (
            "Evaluation Details"
          )
        } 
      />
      
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-600">Loading evaluation details...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-500">{error}</p>
            <p className="text-gray-600 mt-2">Please try again or go back to evaluations.</p>
          </div>
        ) : evaluationData ? (
          <div>
            {/* Sticky header with tabs */}
            <div className="sticky top-[76px] z-30 bg-white shadow-sm">
              <div className="border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <nav className="flex -mb-px">
                    {renderTabButton('questionnaire', 'Questionnaire')}
                    {renderTabButton('challenge', 'Challenge')}
                    {renderTabButton('settings', 'Settings')}
                  </nav>
                  <div className="flex flex-wrap gap-2 items-center pr-4">
                    <a
                      href={`https://eval.tendent.ai/evaluation/${getEvaluationId()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors inline-block"
                    >
                      Evaluation Permalink
                    </a>
                    {evaluationData?.job_id && (
                      <a
                        href={`/job-details?jid=${evaluationData.job_id}`}
                        className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors inline-block"
                      >
                        Job Page
                      </a>
                    )}
                    {evaluationData?.talent_id && (
                      <a
                        href={`/talentdetails?tid=${evaluationData.talent_id}`}
                        className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors inline-block"
                      >
                        Talent Page
                      </a>
                    )}
                    {evaluationData.type && (
                      <span className="text-xs text-gray-500">
                        Type: {evaluationData.type}
                      </span>
                    )}
                    {evaluationData.status && (
                      <EvaluationStatus status={evaluationData.status} showDot={true} size="sm" />
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Tab content */}
            <div className="p-6" key={activeTab}>
            {activeTab === 'questionnaire' && (
              <div>
                {evaluationData?.questionnaires && evaluationData.questionnaires.length > 0 ? (
                  <EvaluationQuestionnaire 
                    questionnaire={evaluationData.questionnaires[0]}
                    showHeader={true}
                  />
                ) : (
                  <div className="flex items-center justify-center h-64">
                    <p className="text-gray-500">No questionnaire data available for this evaluation.</p>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'challenge' && (
              <div>
                {evaluationData?.challenges && evaluationData.challenges.length > 0 ? (
                  <EvaluationChallenge 
                    challenge={evaluationData.challenges[0]}
                    evaluationType={evaluationData.type}
                    showHeader={true}
                  />
                ) : (
                  <div className="flex items-center justify-center h-64">
                    <p className="text-gray-500">No challenge data available for this evaluation.</p>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'settings' && (
              <EvaluationSettings 
                evaluationId={getEvaluationId()}
                evaluationData={evaluationData}
                onRefresh={fetchEvaluationDetails}
              />
            )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600">No evaluation data available.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EvaluationDetails;
